import type { RecordingDurationBucket, RecordingDurationResponse } from "../types/api";
import { MetricCard } from "./MetricCard";

interface RecordingDurationDistributionProps {
  data: RecordingDurationResponse;
}

const fmtSeconds = (v: number) => `${v.toFixed(1)}s`;

function BucketRow({
  title,
  bucket,
  capSeconds,
}: {
  title: string;
  bucket: RecordingDurationBucket;
  capSeconds: number;
}) {
  // p95/p99/max warn when they sit at or above the tier's own hard cap —
  // the checkpoint's "right-censored tail" case (checkpoint
  // 2026-07-25-v2m-projekt-metryk-12-sygnalow.md, M-B): a value pinned to
  // the cap means the recording engine cut it off, not that the user
  // stopped naturally there.
  const nearCap = (v: number) => v >= capSeconds - 1;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-slate-700">
        {title} <span className="text-slate-400 font-normal">({bucket.count} nagrań)</span>
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <MetricCard label="p50" value={fmtSeconds(bucket.p50)} />
        <MetricCard label="p90" value={fmtSeconds(bucket.p90)} />
        <MetricCard label="p95" value={fmtSeconds(bucket.p95)} warn={nearCap(bucket.p95)} />
        <MetricCard label="p99" value={fmtSeconds(bucket.p99)} warn={nearCap(bucket.p99)} />
        <MetricCard label="Max" value={fmtSeconds(bucket.max)} warn={nearCap(bucket.max)} />
      </div>
    </div>
  );
}

/**
 * M-B panel (checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md):
 * percentile distribution of recorded voice length, split by account-tier
 * recording cap (Free 180s, paid plans 300s — M-F-R3). Deliberately no
 * average tile: a single hour-long outlier barely moves a mean but fully
 * dominates p99/max, exactly the number this panel exists to surface (see
 * backend `compute_percentile_summary` docstring).
 *
 * A "warn" (amber) tile at p95/p99/max means that value sits at or near the
 * tier's own hard cap — a right-censored reading (the recording engine cut
 * it off), not proof the user wanted to stop there. This panel does NOT
 * claim to show a "hit the limit" counter (M-C) — see
 * `get_recording_duration_distribution`'s backend docstring for why that
 * specific derivation is a known anti-pattern the checkpoint itself warns
 * against, and where the real M-C signal lives instead.
 */
export function RecordingDurationDistribution({ data }: RecordingDurationDistributionProps) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">
        Ostatnie {data.days} {data.days === 1 ? "doba" : "dni"} — mediana i ogon rozkładu, nie
        średnia (jedno godzinne nagranie potrafi zgubić się w średniej, nie w p99/max).
      </p>
      <BucketRow title="Wszystkie konta" bucket={data.overall} capSeconds={300} />
      <BucketRow title="Free (limit 180s)" bucket={data.free} capSeconds={180} />
      <BucketRow title="Płatne (limit 300s)" bucket={data.paid} capSeconds={300} />
    </div>
  );
}
